import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  CHECKOUT_B2B_SHIPPING,
  CHECKOUT_KLARNA_PAYMENT,
  CHECKOUT_KLARNA_SHIPPING,
  CHECKOUT_PICK_UP_PAYMENT,
  CHECKOUT_PICK_UP_SHIPPING,
} from '../other/CheckoutConstants';
import { CollectIcon, DeliveryIcon } from '../other/CheckoutIcons';
import { translate } from '../../../Services/translation';
import CheckoutError from '../other/CheckoutError';
import { cn } from '../../../Utils/cn';
import { setPaymentAndDelivery as setPaymentAndDeliveryAction } from '../../../Actions/Checkout.action';

const isPickUp = (methodId) => methodId === CHECKOUT_PICK_UP_PAYMENT;

const CheckoutDelivery = ({ checkout }) => {
  const dispatch = useDispatch();
  const { payload, errors = {} } = checkout || {};
  const {
    isBusinessCustomer,
    isDeliveryLoading,
    deliveryMethods,
    selectedDeliveryMethod,
    isPaymentLoading,
    paymentMethods = [],
    selectedPaymentMethod,
  paymentWidget,
  klarnaSelectedShippingOption,
  kssCost,
  orderCurrency
  } = payload || {};

  const filteredPaymentMethods = useMemo(
    () =>
      paymentMethods.filter(
        (paymentMethod) =>
          paymentMethod.id === CHECKOUT_KLARNA_PAYMENT ||
          paymentMethod.id === CHECKOUT_PICK_UP_PAYMENT
      ),
    [paymentMethods]
  );

  const getPaymentMethodById = (id) =>
    paymentMethods.find((x) => x && x.id === id);

  const setPaymentAndDeliveryB2B = () => {
    const selectedDeliveryMethodId = selectedDeliveryMethod.id;
    const selectedPaymentMethodId = selectedPaymentMethod.id;

    const b2bShippingMethod = deliveryMethods.find(
      (x) => x.id === CHECKOUT_B2B_SHIPPING
    );

    // If selected payment or delivery method is not set
    if (!selectedPaymentMethodId || !selectedDeliveryMethodId) {
      dispatch(
        setPaymentAndDeliveryAction(selectedPaymentMethod, b2bShippingMethod)
      );
    }

    // "Force user" to use B2B checkout and delivery methods
    if (selectedDeliveryMethodId !== b2bShippingMethod.id) {
      dispatch(
        setPaymentAndDeliveryAction(selectedPaymentMethod, b2bShippingMethod)
      );
    }
  };

  const setPaymentAndDelivery = (paymentMethod) => {
    if (!payload) {
      return;
    }

    if (isBusinessCustomer) {
      setPaymentAndDeliveryB2B();
      return;
    }

    const selectedPaymentMethodId = selectedPaymentMethod.id;
    const selectedDeliveryMethodId = selectedDeliveryMethod.id;

    // Is B2C
    // Get the B2C delivery methods
    const klarnaShippingMethod = deliveryMethods.find(
      (x) => x && x.id === CHECKOUT_KLARNA_SHIPPING
    );
    const pickUpShippingMethod = deliveryMethods.find(
      (x) => x && x.id === CHECKOUT_PICK_UP_SHIPPING
    );

    // Has user clicked "Click and collect" when adding last product?
    const buyAndCollectClicked = localStorage.getItem('isBuyAndCollectButton');

    // If no selected payment or delivery options (i.e. first time checkout)
    if (
      !selectedPaymentMethodId ||
      selectedDeliveryMethodId == '00000000-0000-0000-0000-000000000000'
    ) {
      if (buyAndCollectClicked && buyAndCollectClicked === '1') {
        dispatch(
          setPaymentAndDeliveryAction(
            getPaymentMethodById(CHECKOUT_PICK_UP_PAYMENT),
            pickUpShippingMethod
          )
        );
        return;
      } else {
        dispatch(
          setPaymentAndDeliveryAction(
            getPaymentMethodById(CHECKOUT_KLARNA_PAYMENT),
            klarnaShippingMethod
          )
        );
        return;
      }
    }

    // User clicked on "Click and collect" when adding last product
    if (selectedPaymentMethodId !== CHECKOUT_PICK_UP_PAYMENT && buyAndCollectClicked === '1'
    ) {
      dispatch(setPaymentAndDeliveryAction(getPaymentMethodById(CHECKOUT_PICK_UP_PAYMENT), pickUpShippingMethod)
      );
      return;
    }

    // Klarna checkout is picked, but not initialized paymentWidget
    if (selectedPaymentMethodId === CHECKOUT_KLARNA_PAYMENT && !paymentWidget) {
      dispatch(
        setPaymentAndDeliveryAction(selectedPaymentMethod, klarnaShippingMethod)
      );
      return;
    }

    // User changed payment method
    if (
      selectedPaymentMethodId &&
      paymentMethod.id !== selectedPaymentMethodId
    ) {
      if (paymentMethod.id === CHECKOUT_KLARNA_PAYMENT) {
        dispatch(
          setPaymentAndDeliveryAction(paymentMethod, klarnaShippingMethod)
        );
        return;
      }
      if (paymentMethod.id === CHECKOUT_PICK_UP_PAYMENT) {
        dispatch(
          setPaymentAndDeliveryAction(paymentMethod, pickUpShippingMethod)
        );
        return;
      }
    }
  };

  useEffect(() => {
    if (selectedPaymentMethod) {
      setPaymentAndDelivery(selectedPaymentMethod);
    }
  }, []);

  if (isBusinessCustomer || !filteredPaymentMethods?.length) {
    return null;
  }

  return (
    <div className="checkout-section">
      <h2 className="heading-2 mb-8">
        {translate('checkout.payment_select.title')}
      </h2>

      <ul
        className={cn(
          'mb-8 last:mb-0',
          filteredPaymentMethods.length > 1 && 'grid gap-2 sm:grid-cols-2',
          filteredPaymentMethods.length === 2 &&
            'sm:bg-grey-lighter sm:gap-0 sm:rounded-full'
        )}
      >
        {filteredPaymentMethods.map((method) => {
          const pickUp = isPickUp(method.id);
          const isSelected =
            !!selectedPaymentMethod && method.id === selectedPaymentMethod.id;
          const isBusy = !!(isDeliveryLoading || isPaymentLoading);
          return (
            <li key={method.id}>
              <button
                className={cn(
                  'text-bold bg-grey-lighter flex size-full items-center justify-center gap-4 rounded-full px-8 py-6 text-left disabled:cursor-wait',
                  isSelected && 'bg-accent text-white'
                )}
                onClick={() => setPaymentAndDelivery(method)}
                disabled={isBusy}
                type="button"
              >
                {pickUp ? (
                  <CollectIcon className="w-6 shrink-0" />
                ) : (
                  <DeliveryIcon className="w-6 shrink-0" />
                )}
                <span className="grid gap-1">
                  <span className="body body_bold leading-base">
                    {translate(
                      pickUp
                        ? 'checkout.payment_select.direct_pay'
                        : 'checkout.payment_select.klarna_pay'
                    )}
                  </span>
                  <span className="body-sm leading-base">
                    {translate(
                      pickUp
                        ? 'checkout.payment_select.free_shipping'
                        : 'checkout.payment_select.shipping_from'
                    )}
                    { !pickUp && ` ${kssCost}`
                    }
                    </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <CheckoutError errors={errors?.selectedPaymentMethod} />
    </div>
  );
};

export default CheckoutDelivery;
